import type { ReactNode } from 'react';
import React, { useState } from 'react';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import ChevronDownIcon from '~/components/icons/ChevronDownIcon';
import Collapse from '~/components/ui/atoms/Collapse';
import cn from '~/Utils/cn';

type Tone = 'TEAL' | 'PINK';

interface ExpandingCardProps {
  title: ReactNode;
  label: string;
  children: ReactNode;
  tone?: Tone;
}

const TONE_CLASS_NAME: Record<Tone, string> = {
  TEAL: 'border-s-teal-600',
  PINK: 'border-s-pink-600',
};

export const TONES = {
  TEAL: 'TEAL',
  PINK: 'PINK',
} as const satisfies Record<string, Tone>;

const ExpandingCard = ({ title, label, children, tone = TONES.TEAL }: ExpandingCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => setExpanded((prev) => !prev);

  return (
    <div className="rounded-xl border border-s-0 border-solid border-slate-200">
      <div className={cn('rounded-xl border border-0 border-s-[6px] border-solid shadow-xs', TONE_CLASS_NAME[tone])}>
        {/* The whole header row toggles on click as a convenience for mouse users; the chevron is the one
            real control (focusable, labelled, aria-expanded), so keyboard and screen-reader users land on a
            single button per card rather than on the entire row. */}
        <div onClick={toggleExpanded} className="flex cursor-pointer items-center gap-x-12 p-16">
          <div className="flex-1">{title}</div>
          <IconButton
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
            onClick={(event) => {
              event.stopPropagation(); // the row's own handler would otherwise toggle it straight back
              toggleExpanded();
            }}
          >
            <ChevronDownIcon size={20} iconColor="currentColor" className={cn({ 'rotate-180': expanded })} />
          </IconButton>
        </div>
        <Collapse in={expanded} unmountOnExit>
          <div className="px-16 pb-16 text-base">{children}</div>
        </Collapse>
      </div>
    </div>
  );
};

export default ExpandingCard;
